import { Checkbox, FormControl, FormHelperText, InputLabel, MenuItem, Select } from '@material-ui/core';
import { getIn } from 'formik';
import React, { useCallback, useMemo } from 'react';
import { Hint } from 'v2/components/Shared/Hint';

import type { FieldProps } from 'formik';

interface ISelectOption {
  label: string;
  value: string | number;
  disabled?: boolean;
}

interface IProps extends FieldProps {
  label?: string;
  labelItens?: string;
  multiple?: boolean;
  native?: boolean;
  options: ISelectOption[];
  loading?: boolean;
  emptyOption?: string;
  onChange?: (e: React.ChangeEvent<{ name?: string; value: unknown }>) => void;
  helperText?: React.ReactNode;
  placeholder?: string;
}

const MultipleValues = ({ options = [], field, label, labelItens = 'itens', loading, placeholder = 'Selecione', ...props }: IProps) => {
  const hasNoOptions = !options?.length;

  const selecteds = useMemo(() => options?.filter((op) => (field.value || []).includes(op.value)), [field.value, options]);

  const renderMultipleValues = () => {
    if (loading) {
      return 'Carregando...';
    }

    if (hasNoOptions) {
      return `Não há ${label?.toLowerCase()}`;
    }

    if (!selecteds.length) {
      return placeholder;
    }

    return `${selecteds.length} ${labelItens} selecionados`;
  };

  return (
    <Select fullWidth {...props} {...field} renderValue={renderMultipleValues} displayEmpty={true} disabled={hasNoOptions}>
      {options?.map((op) => (
        <MenuItem key={op.value} value={op.value} disabled={op.disabled}>
          <Checkbox checked={(field.value || []).includes(op.value)} />
          {op.label}
        </MenuItem>
      ))}
    </Select>
  );
};

const SingleValue = ({ options = [], field, label, loading, emptyOption, placeholder = 'Selecione uma opção', ...props }: IProps) => {
  const hasNoOptions = !options?.length;

  const renderValues = () => {
    if (loading) {
      return 'Carregando...';
    }

    if (hasNoOptions) {
      return `Não há ${label?.toLowerCase()}`;
    }

    const selected = options?.find((op) => op.value === field.value);

    return selected?.label || placeholder;
  };

  const handleChange = useCallback(
    (e: React.ChangeEvent<{ name?: string; value: unknown }>) => {
      props?.onChange?.(e);
      field.onChange(e);
    },
    [field, props]
  );

  return (
    <Select
      fullWidth
      {...props}
      {...field}
      onChange={handleChange}
      renderValue={renderValues}
      displayEmpty
      MenuProps={{
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'left',
        },
        transformOrigin: {
          vertical: 'top',
          horizontal: 'left',
        },
        getContentAnchorEl: null,
      }}
    >
      {!!emptyOption && <MenuItem value=''>{emptyOption}</MenuItem>}
      {options?.map((op) => (
        <MenuItem key={op.value} value={op.value} disabled={op.disabled}>
          {op.label}
        </MenuItem>
      ))}
    </Select>
  );
};

export const SelectFormField = ({ field, form, label, helperText, ...props }: IProps) => {
  const errorText = getIn(form.errors, field.name);

  return (
    <Hint inputName={field.name}>
      <FormControl fullWidth error={!!errorText}>
        {label && <InputLabel>{label}</InputLabel>}
        {props.multiple ? (
          <MultipleValues field={field} form={form} label={label} {...props} />
        ) : (
          <SingleValue field={field} form={form} label={label} {...props} />
        )}
        {errorText && <FormHelperText>{errorText}</FormHelperText>}
        {!errorText && helperText && <FormHelperText>{helperText}</FormHelperText>}
      </FormControl>
    </Hint>
  );
};
